import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DecimalRating from '../common/StarRating';

// Skeleton component matching SocialFeed skeleton style for loading
function ReviewListSkeleton() {
  return (
    <div className="max-w-3xl mx-auto p-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="border border-gray-300 rounded-lg p-4 mb-4 bg-white shadow-sm animate-pulse">
          <div className="flex justify-between items-center mb-2">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
            <div className="h-6 bg-gray-200 rounded w-12"></div>
          </div>
          <div className="h-16 bg-gray-200 rounded mb-3"></div>
          <div className="flex justify-between items-center text-sm">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

function ReviewItem({ review, onEdit, onDelete, user }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editData, setEditData] = useState({
    name: review.name,
    review: review.review || '',
    rating: review.rating,
    visibility: review.visibility
  });
  const textareaRef = useRef(null);

  // Auto-resize function
  const autoResize = (textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(120, textarea.scrollHeight) + 'px';
    }
  };

  // Auto-expand textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      setTimeout(() => autoResize(textareaRef.current), 10);
    }
  }, [isEditing]);

  const handleEdit = async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/reviews/${review.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'X-User-ID': user.id
        },
        body: JSON.stringify(editData)
      });

      if (!response.ok) throw new Error('Failed to update review');

      setIsEditing(false);
      if (onEdit) onEdit();
    } catch (err) {
      console.error('Error updating review:', err);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/reviews/${review.id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': API_KEY,
          'X-User-ID': user.id
        }
      });

      if (!response.ok) throw new Error('Failed to delete review');

      setShowDeleteConfirm(false);
      if (onDelete) onDelete();
    } catch (err) {
      console.error('Error deleting review:', err);
      setShowDeleteConfirm(false);
    }
  };

  if (isEditing) {
    return (
      <div className="border border-gray-300 rounded-lg p-6 mb-4 bg-white shadow-sm">
        <div className="mb-6">
          <label className="block text-gray-700 font-semibold mb-2 text-lg">Film/TV Title</label>
          <input
            type="text"
            value={editData.name}
            onChange={(e) => setEditData({...editData, name: e.target.value})}
            className="w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-blue-500 text-gray-900 font-semibold"
          />
        </div>
        <div className="mb-6">
          <label className="block text-gray-700 font-semibold mb-2 text-lg">Review</label>
          <textarea
            ref={textareaRef}
            value={editData.review}
            onChange={(e) => {
              setEditData({...editData, review: e.target.value});
              autoResize(e.target);
            }}
            onInput={(e) => autoResize(e.target)}
            className="w-full px-4 py-3 text-lg border-2 rounded-lg focus:outline-none focus:border-blue-500 text-gray-900 resize-none overflow-hidden"
            style={{ minHeight: '120px' }}
            rows={4}
            placeholder="Write your review..."
          />
        </div>
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <DecimalRating
              value={editData.rating || 0}
              onChange={(newRating) => setEditData({...editData, rating: newRating})}
              label="Rating (0.1-10.0)"
            />
          </div>
          <div className="flex-1 flex items-center">
            <label className="flex items-center gap-3 text-lg font-semibold text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={editData.visibility === 'public'}
                onChange={(e) => setEditData({...editData, visibility: e.target.checked ? 'public' : 'private'})}
                className="w-6 h-6 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500"
              />
              Make review public
            </label>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleEdit}
            className="flex-1 min-h-[48px] px-6 py-3 bg-blue-500 text-white text-lg font-semibold rounded-lg hover:bg-blue-600 transition-colors"
          >
            Save Changes
          </button>
          <button
            onClick={() => setIsEditing(false)}
            className="flex-1 min-h-[48px] px-6 py-3 bg-gray-500 text-white text-lg font-semibold rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group border border-gray-300 rounded-lg p-4 mb-4 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-xl md:text-lg font-semibold text-indigo-600 leading-tight">{review.name}</h3>
          {review.imdb_url && (
            <a
              href={review.imdb_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-400 hover:bg-yellow-500 text-black text-xs font-bold rounded transition-colors"
              title="View on IMDb"
            >
              IMDb
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-yellow-500 font-bold">{review.rating.toFixed(1)}</span>
          <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 text-gray-500 hover:text-indigo-600 transition-colors rounded"
              title="Edit review"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 text-gray-500 hover:text-red-600 transition-colors rounded"
              title="Delete review"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <p className="text-gray-700 whitespace-pre-line">
        {review.review || <i className="text-gray-400">No review text provided</i>}
      </p>
      <div className="mt-3 flex justify-between items-center text-sm text-gray-500">
        <span>{new Date(review.created_at).toLocaleDateString()}</span>
        {review.updated_at && review.updated_at !== review.created_at && (
          <span className="text-xs bg-gray-100 px-2 py-1 rounded">edited</span>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">🗑️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Delete Review?</h3>
              <p className="text-gray-600">
                This will permanently delete your review for <span className="font-semibold">"{review.name}"</span>
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDelete}
                className="min-h-[48px] px-6 py-3 bg-red-500 text-white text-lg font-semibold rounded-lg hover:bg-red-600 transition-colors"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="min-h-[48px] px-6 py-3 bg-gray-200 text-gray-800 text-lg font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReviewList({ user }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReviews = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/v1/reviews/me`, {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-User-ID': user.id
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('API Error Response:', errorData);
        throw new Error(errorData.detail || 'Failed to fetch reviews');
      }
      const data = await response.json();
      setReviews(data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [user]);

  if (loading) {
    return <ReviewListSkeleton />;
  }
  if (error) return <div className="text-center text-red-600 py-8">Error: {error}</div>;
  if (!reviews.length) return (
    <div className="text-center py-8">
      <span className="text-sm text-white font-space-grotesk text-center">
        You haven't written any reviews yet - <Link to="/reviews" className="text-indigo-400 hover:text-indigo-600 underline">add your first review.</Link>
      </span>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h2 className="text-xl font-bold mb-2 text-cling-pink">My Reviews ({reviews.length})</h2>
      {reviews.map((review) => (
        <ReviewItem
          key={review.id}
          review={review}
          user={user}
          onEdit={fetchReviews}
          onDelete={fetchReviews}
        />
      ))}
    </div>
  );
}

